import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { Callout, errorText, Overline } from "@amber/ui";
import type { Candidate } from "../../contracts/index";
import { candidatesQuery } from "../queries";
import { CandidatePortrait } from "./CandidatePortrait";

export function CandidateList({
  query,
  onChoose,
  onManual,
}: {
  query: string;
  onChoose: (c: Candidate) => void;
  onManual: () => void;
}) {
  const result = useQuery({
    ...candidatesQuery(query),
    enabled: query.length > 0,
  });
  if (query.length === 0) return null;
  if (result.isPending) return <p className="mt-4 text-md text-muted">Searching Wikipedia and Wikidata…</p>;
  if (result.isError)
    return (
      <Callout tone="attention" className="mt-4" title="Couldn't search Wikipedia and Wikidata">
        {errorText(result.error)}
      </Callout>
    );
  const data = result.data;
  return (
    <section className="mt-4" aria-label="Candidates">
      {data.link_error ? <Callout tone="waiting" className="mb-2" title={data.link_error} /> : null}
      <Overline as="h3" className="m-0 mb-1.5">
        {data.candidates.length} candidate
        {data.candidates.length === 1 ? "" : "s"} for “{data.query}”
      </Overline>
      <ul className="m-0 list-none overflow-hidden rounded-xl border border-divider p-0">
        {data.candidates.map((c) => (
          <li key={c.wikidata_id} className="border-b border-divider">
            <button
              type="button"
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-surface-2"
              onClick={() => {
                onChoose(c);
              }}
            >
              <CandidatePortrait name={c.name} url={c.image_url} />
              <span className="min-w-0 flex-1">
                <b className="block font-strong text-fg">{c.name}</b>
                <span className="block text-md text-muted">
                  {[c.description, c.current_role].filter(Boolean).join(" · ") || "No description"}
                </span>
                <span className="block text-xs text-muted">
                  {[
                    c.birth_date ? `Born ${c.birth_date}` : "Birth date unknown",
                    c.edition ? `${c.edition}.wikipedia` : "No article",
                    "Wikidata",
                  ].join(" · ")}
                  {c.existing ? ` · already a profile (${c.existing.state_label})` : ""}
                </span>
              </span>
              <span className="inline-flex items-center gap-1 text-md font-strong text-link">
                Choose <ArrowRight size={13} aria-hidden="true" />
              </span>
            </button>
          </li>
        ))}
        <li>
          <button
            type="button"
            className="flex w-full items-center gap-3 bg-surface-2 px-3 py-2.5 text-left hover:bg-surface-3"
            onClick={onManual}
          >
            <span className="min-w-0 flex-1">
              <b className="block font-strong text-fg">None of these — create manually</b>
              <span className="block text-md text-muted">
                For people without a suitable article. Unknown details stay empty.
              </span>
            </span>
            <ArrowRight size={13} aria-hidden="true" className="text-muted" />
          </button>
        </li>
      </ul>
    </section>
  );
}
